import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

// gloss
const cardVariants = cva(
  'group/card flex flex-col gap-4 overflow-hidden rounded-lg bg-card py-4 text-card-foreground text-xs/relaxed ring-1 ring-foreground/10 has-[>img:first-child]:pt-0 data-[size=sm]:gap-3 data-[size=sm]:py-3 data-[size=lg]:gap-6 data-[size=lg]:py-6 *:[img:first-child]:rounded-t-lg *:[img:last-child]:rounded-b-lg',
  {
    variants: {
      tone: {
        default: '',
        muted: 'bg-muted/30',
        primary: 'bg-primary/5 ring-primary/20',
        success: 'bg-success/5 ring-success/20',
        warning: 'bg-warning/5 ring-warning/20',
        info: 'bg-info/5 ring-info/20',
        destructive: 'bg-destructive/5 ring-destructive/20',
      },
    },
    defaultVariants: { tone: 'default' },
  },
);

type CardProps = React.ComponentProps<'div'> &
  VariantProps<typeof cardVariants> & {
    size?: 'default' | 'sm' | 'lg';
  };

function Card({ className, size = 'default', tone = 'default', ...props }: CardProps) {
  return <div data-slot="card" data-size={size} className={cn(cardVariants({ tone, className }))} {...props} />;
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-lg px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] group-data-[size=lg]/card:px-6 group-data-[size=sm]/card:px-3 [.border-b]:pb-4 group-data-[size=lg]/card:[.border-b]:pb-6 group-data-[size=sm]/card:[.border-b]:pb-3',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="card-title" className={cn('font-medium text-sm', className)} {...props} />;
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="card-description" className={cn('text-muted-foreground text-xs/relaxed', className)} {...props} />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn('col-start-2 row-span-2 row-start-1 self-start justify-self-end', className)}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-4 group-data-[size=lg]/card:px-6 group-data-[size=sm]/card:px-3', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        'flex items-center rounded-b-lg px-4 group-data-[size=lg]/card:px-6 group-data-[size=sm]/card:px-3 [.border-t]:pt-4 group-data-[size=lg]/card:[.border-t]:pt-6 group-data-[size=sm]/card:[.border-t]:pt-3',
        className,
      )}
      {...props}
    />
  );
}

export type { CardProps };
export { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, cardVariants };
